import React, { useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Handle,
  Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Custom Node to support handles on all 4 sides so edges don't disappear
const BoxNode = ({ data }) => {
  return (
    <div style={{ padding: '0 8px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Top} id="top" style={{ opacity: 0 }} />
      
      <Handle type="target" position={Position.Right} id="right" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} id="right" style={{ opacity: 0 }} />
      
      <Handle type="target" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      
      <Handle type="target" position={Position.Left} id="left" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Left} id="left" style={{ opacity: 0 }} />
      
      {data.label}
    </div>
  );
};

const nodeTypes = {
  box: BoxNode,
};

const CoreBusinessStrategyFlow = ({ data }) => {
  const nodes = useMemo(() => {
    if (!data) return [];
    
    // --- Premium Styles ---
    const groupStyle = { 
      width: 360, 
      height: 750, 
      backgroundColor: '#f8fafc', 
      border: '1px solid #e2e8f0', 
      borderRadius: '24px', 
      zIndex: -1,
      boxShadow: '0 10px 30px rgba(0,0,0,0.02)'
    };

    const titleStyle = { 
      background: 'transparent', 
      border: 'none', 
      width: 340, 
      boxShadow: 'none', 
      textAlign: 'center',
      fontSize: '16px',
      fontWeight: '700'
    };

    const nodeStyle = {
      borderRadius: '12px',
      padding: '12px 0',
      fontSize: '14px',
      fontWeight: '600',
      textAlign: 'center',
      border: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    const coreStyle = { ...nodeStyle, width: 140, background: '#ffffff', color: '#1e3a8a', border: '1px solid #e2e8f0' };
    const highlightStyle = { ...nodeStyle, width: 160, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' };
    
    const channelStyle = { ...nodeStyle, width: 220, background: '#ffffff', color: '#334155', border: '1px solid #e2e8f0' };
    const subStyle = { ...nodeStyle, width: 220, color: '#ffffff', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' };

    return [
      // 1. HOSPITAL SYSTEM GROUP BACKGROUND
      {
        id: 'hospital_system',
        position: { x: 300, y: 50 },
        data: { label: '' },
        style: groupStyle,
        draggable: false, selectable: false, type: 'box'
      },
      {
        id: 'hs_title',
        position: { x: 310, y: 70 },
        data: { label: <span style={{color: '#1e40af'}}>{data?.hospitalSystem?.title || 'HỆ THỐNG BỆNH VIỆN'}</span> },
        style: titleStyle,
        draggable: false, selectable: false, type: 'box'
      },
      
      {
        id: 'label_core_biz',
        position: { x: 310, y: 130 },
        data: { label: <div style={{color: '#475569', fontSize: '14px', letterSpacing: '1.5px', fontWeight: '600'}}>CORE BUSINESS</div> },
        style: { ...titleStyle, width: 340, textAlign: 'center' },
        draggable: false, selectable: false, type: 'box'
      },

      // Core Business Nodes
      {
        id: 'welcome',
        position: { x: 400, y: 170 },
        data: { label: data?.hospitalSystem?.core?.entrance || 'Welcome' },
        style: highlightStyle,
        type: 'box'
      },
      {
        id: 'tuvan',
        position: { x: 320, y: 260 },
        data: { label: data?.hospitalSystem?.core?.diagnostics?.[0] || 'Tư vấn' },
        style: coreStyle,
        type: 'box'
      },
      {
        id: 'canlamsan',
        position: { x: 500, y: 260 },
        data: { label: data?.hospitalSystem?.core?.diagnostics?.[1] || 'Cận lâm sàn' },
        style: coreStyle,
        type: 'box'
      },
      {
        id: 'chandoan',
        position: { x: 320, y: 340 },
        data: { label: data?.hospitalSystem?.core?.diagnostics?.[2] || 'Chẩn đoán' },
        style: coreStyle,
        type: 'box'
      },
      {
        id: 'xetnghiem',
        position: { x: 500, y: 340 },
        data: { label: data?.hospitalSystem?.core?.diagnostics?.[3] || 'Xét nghiệm' },
        style: coreStyle,
        type: 'box'
      },
      
      {
        id: 'medica',
        position: { x: 415, y: 440 },
        data: { label: data?.hospitalSystem?.core?.center || '+ MEDICA' },
        style: { ...nodeStyle, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white', width: 130, height: 130, borderRadius: '50%', fontSize: '18px', boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)' },
        type: 'box'
      },

      // Post Medical
      {
        id: 'label_post_medical',
        position: { x: 290, y: 600 },
        data: { label: <div style={{color: '#475569', fontSize: '14px', letterSpacing: '1.5px', fontWeight: '600'}}>Post-medical Accomodation</div> },
        style: { ...titleStyle, width: 380, textAlign: 'center', backgroundColor: '#f8fafc', padding: '4px 12px', borderRadius: '8px', zIndex: 10 },
        draggable: false, selectable: false, type: 'box'
      },
      {
        id: 'patient',
        position: { x: 320, y: 650 },
        data: { label: data?.hospitalSystem?.postMedical?.items?.[0] || 'Patient' },
        style: { ...coreStyle, background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' },
        type: 'box'
      },
      {
        id: 'family',
        position: { x: 500, y: 650 },
        data: { label: data?.hospitalSystem?.postMedical?.items?.[1] || 'Family' },
        style: { ...coreStyle, background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' },
        type: 'box'
      },

      // 2. DISTRIBUTION CHANNELS
      {
        id: 'ch_title',
        position: { x: 10, y: 40 },
        data: { label: <span style={{color: '#1e3a8a'}}>{data?.channels?.title || 'KÊNH PHÂN PHỐI ĐỒNG BỘ'}</span> },
        style: { ...titleStyle, width: 220, textAlign: 'center' },
        draggable: false, selectable: false, type: 'box'
      },
      {
        id: 'ehospital',
        position: { x: 10, y: 100 },
        data: { label: data?.channels?.items?.[0] || 'E-hospital' },
        style: { ...channelStyle, background: '#f1f5f9' },
        type: 'box'
      },
      {
        id: 'walkin',
        position: { x: 10, y: 170 },
        data: { label: data?.channels?.items?.[1] || 'Walk in' },
        style: channelStyle,
        type: 'box'
      },
      {
        id: 'clinic',
        position: { x: 10, y: 240 },
        data: { label: data?.channels?.items?.[2] || 'Clinic' },
        style: { ...channelStyle, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
        type: 'box'
      },
      {
        id: 'privatedoc',
        position: { x: 10, y: 310 },
        data: { label: data?.channels?.items?.[3] || 'Hợp tác Bác sĩ tư' },
        style: channelStyle,
        type: 'box'
      },

      // 3. SUBSIDIARIES (MIDDLE)
      {
        id: 'waypoint_sub',
        position: { x: 690, y: 395 },
        data: { label: '' },
        style: { width: 12, height: 12, background: '#94a3b8', borderRadius: '50%', border: '2px solid #ffffff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
        draggable: false, selectable: false, type: 'box'
      },
      {
        id: 'sub_duoc',
        position: { x: 720, y: 250 },
        data: { label: data?.subsidiaries?.items?.[0] || 'Công ty dược phẩm' },
        style: { ...subStyle, background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)' },
        type: 'box'
      },
      {
        id: 'sub_dvtm',
        position: { x: 720, y: 330 },
        data: { label: data?.subsidiaries?.items?.[1] || 'Cty DVTM' },
        style: { ...subStyle, background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)' },
        type: 'box'
      },
      {
        id: 'sub_ktc',
        position: { x: 720, y: 410 },
        data: { label: data?.subsidiaries?.items?.[2] || 'Trung tâm y khoa KTC' },
        style: { ...subStyle, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' },
        type: 'box'
      },
      {
        id: 'sub_pk',
        position: { x: 720, y: 490 },
        data: { label: data?.subsidiaries?.items?.[3] || 'Các PK Bác sĩ trong BV' },
        style: { ...subStyle, background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' },
        type: 'box'
      },

      // 4. PUBLIC HOSPITALS (RIGHT)
      {
        id: 'public_hospital',
        position: { x: 1000, y: 50 },
        data: { label: '' },
        style: { ...groupStyle, width: 300, backgroundColor: 'rgba(74, 222, 128, 0.03)', border: '2px dashed #86efac' },
        draggable: false, selectable: false, type: 'box'
      },
      {
        id: 'ph_title',
        position: { x: 1010, y: 70 },
        data: { label: <span style={{color: '#166534'}}>{data?.publicHospitals?.title || 'BỆNH VIỆN ĐA KHOA CÔNG LẬP'}</span> },
        style: { ...titleStyle, width: 280 },
        draggable: false, selectable: false, type: 'box'
      },
      {
        id: 'ph_c1',
        position: { x: 1120, y: 260 },
        data: { label: '' },
        style: { width: 60, height: 60, borderRadius: '50%', background: '#86efac', border: 'none', boxShadow: '0 4px 10px rgba(134,239,172,0.4)' },
        type: 'box'
      },
      {
        id: 'ph_c2',
        position: { x: 1120, y: 340 },
        data: { label: '' },
        style: { width: 60, height: 60, borderRadius: '50%', background: '#93c5fd', border: 'none', boxShadow: '0 4px 10px rgba(147,197,253,0.4)' },
        type: 'box'
      }
    ];
  }, [data]);

  const edges = useMemo(() => {
    if (!data) return [];
    
    const defaultEdge = {
      type: 'bezier',
      animated: true,
      style: { stroke: '#94a3b8', strokeWidth: 2, strokeDasharray: '5,5' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' }
    };

    const coreEdge = {
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#3b82f6', strokeWidth: 2, strokeDasharray: '5,5' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }
    };
    
    const medicaEdge = {
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' }
    };

    const subEdge = {
      type: 'bezier',
      animated: true,
      style: { stroke: '#22c55e', strokeWidth: 2, strokeDasharray: '5,5' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e' }
    };

    return [
      // Channels to Hospital (Left to Right)
      { id: 'e-ehos-wel', source: 'ehospital', target: 'welcome', ...defaultEdge, sourceHandle: 'right', targetHandle: 'left' },
      { id: 'e-walkin-wel', source: 'walkin', target: 'welcome', ...defaultEdge, sourceHandle: 'right', targetHandle: 'left' },
      { id: 'e-cli-wel', source: 'clinic', target: 'welcome', ...defaultEdge, style: { stroke: '#3b82f6', strokeWidth: 2, strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }, sourceHandle: 'right', targetHandle: 'left' },
      { id: 'e-priv-can', source: 'privatedoc', target: 'welcome', ...defaultEdge, sourceHandle: 'right', targetHandle: 'left' },

      // Core Business Flow (Top to Bottom)
      { id: 'e-wel-tuvan', source: 'welcome', target: 'tuvan', ...coreEdge, sourceHandle: 'bottom', targetHandle: 'top' },
      { id: 'e-wel-can', source: 'welcome', target: 'canlamsan', ...coreEdge, sourceHandle: 'bottom', targetHandle: 'top' },
      { id: 'e-tuvan-chan', source: 'tuvan', target: 'chandoan', ...coreEdge, sourceHandle: 'bottom', targetHandle: 'top' },
      { id: 'e-can-xet', source: 'canlamsan', target: 'xetnghiem', ...coreEdge, sourceHandle: 'bottom', targetHandle: 'top' },
      
      { id: 'e-chan-med', source: 'chandoan', target: 'medica', ...medicaEdge, sourceHandle: 'bottom', targetHandle: 'top' },
      { id: 'e-xet-med', source: 'xetnghiem', target: 'medica', ...medicaEdge, sourceHandle: 'bottom', targetHandle: 'top' },
      
      { id: 'e-med-pat', source: 'medica', target: 'patient', ...coreEdge, sourceHandle: 'bottom', targetHandle: 'top' },
      { id: 'e-med-fam', source: 'medica', target: 'family', ...coreEdge, sourceHandle: 'bottom', targetHandle: 'top' },

      // Subsidiaries to Hospitals (Right to Left) via Waypoint
      { id: 'e-duoc-wp', source: 'sub_duoc', target: 'waypoint_sub', targetHandle: 'right', ...defaultEdge, type: 'smoothstep', sourceHandle: 'left' },
      { id: 'e-dvtm-wp', source: 'sub_dvtm', target: 'waypoint_sub', targetHandle: 'right', ...defaultEdge, type: 'smoothstep', sourceHandle: 'left' },
      { id: 'e-ktc-wp', source: 'sub_ktc', target: 'waypoint_sub', targetHandle: 'right', ...defaultEdge, type: 'smoothstep', sourceHandle: 'left' },
      { id: 'e-pk-wp', source: 'sub_pk', target: 'waypoint_sub', targetHandle: 'right', ...defaultEdge, type: 'smoothstep', sourceHandle: 'left' },
      { id: 'e-wp-hs1', source: 'waypoint_sub', target: 'hospital_system', targetHandle: 'right', ...defaultEdge, type: 'smoothstep', sourceHandle: 'left' },
      
      // Subsidiaries to Public Hospitals (Left to Right)
      { id: 'e-duoc-ph1', source: 'sub_duoc', target: 'ph_c1', ...subEdge, sourceHandle: 'right', targetHandle: 'left' },
      { id: 'e-dvtm-ph1', source: 'sub_dvtm', target: 'ph_c2', ...coreEdge, sourceHandle: 'right', targetHandle: 'left' }
    ];
  }, [data]);

  if (!data) return null;

  return (
    <div style={{ width: '100%', height: '800px', background: '#ffffff', borderRadius: '24px', border: '1px solid #f1f5f9', marginTop: '30px', boxShadow: '0 20px 40px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15, minZoom: 0.5, maxZoom: 1.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        panOnScroll={true}
        preventScrolling={false}
      >
        <Background color="#e2e8f0" gap={20} size={1.5} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

export default CoreBusinessStrategyFlow;
